/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { FormattedMessage } from 'react-intl'

import { EuiCallOut } from '@elastic/eui'

import type { ElasticsearchCluster, StackDeployment } from '@modules/ui-types'

import { gte } from '../../lib/semver'

import DeploymentSecurityEditor from './DeploymentSecurityEditor'
import DeploymentSecurityEditorBefore5x from './DeploymentSecurityEditorBefore5x'

import type { FunctionComponent } from 'react'

export type Props = {
  deployment: StackDeployment | null
  cluster?: ElasticsearchCluster | null
}

const DeploymentSecurity: FunctionComponent<Props> = ({ deployment, cluster }) => {
  if (deployment == null) {
    return null
  }

  if (cluster == null) {
    return null
  }

  const { plan } = cluster

  if (!plan.isActive) {
    // in this case we have no plan, so we can't render anything but a warning
    return (
      <EuiCallOut
        color='warning'
        iconType='alert'
        title={
          <FormattedMessage
            id='deployment-security.no-plan.title'
            defaultMessage='Invalid configuration'
          />
        }
      >
        <FormattedMessage
          id='deployment-security.no-plan'
          defaultMessage='This deployment has no active configuration, check the Activity tab for more information.'
        />
      </EuiCallOut>
    )
  }

  if (plan.version && gte(plan.version, `5.0.0`)) {
    return <DeploymentSecurityEditor deployment={deployment} cluster={cluster} />
  }

  return <DeploymentSecurityEditorBefore5x />
}

export default DeploymentSecurity
